import { createContext, useContext, useMemo } from 'react';
import { configureApiClient } from '../shared/lib/apiClient';

const TenantContext = createContext(null);

const DEV_FALLBACK_SLUG = 'demo';

/**
 * Resolves which tenant slug the login screen should target, based on the
 * subdomain in production (e.g. "acme.tgmsaas.com" -> "acme"), or a
 * "?tenant=" query param / hardcoded fallback during local development
 * where everything runs on localhost without real subdomains.
 */
function resolveTenantSlug() {
  if (typeof window === 'undefined') return DEV_FALLBACK_SLUG;

  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('tenant');
  if (fromQuery) return fromQuery;

  const host = window.location.hostname;
  const parts = host.split('.');
  const isLocalHost = host === 'localhost' || host === '127.0.0.1' || parts.length <= 2;
  if (!isLocalHost && parts[0] !== 'www') {
    return parts[0];
  }

  return DEV_FALLBACK_SLUG;
}

export function TenantProvider({ children }) {
  const tenantSlug = useMemo(resolveTenantSlug, []);
  const value = useMemo(() => ({ tenantSlug }), [tenantSlug]);

  // The backend's tenant-resolver middleware needs to know which tenant a
  // request belongs to for every route except login/password-reset-request
  // (which carry tenantSlug in their own JSON body). In production this
  // comes from the subdomain, but local dev typically runs on plain
  // "localhost" with no subdomain, so the backend falls back to this
  // header - see apps/api/src/middleware/tenant-resolver.js.
  //
  // This is called directly during render, NOT inside a useEffect: React
  // fires child effects before parent effects, and AuthProvider (a child of
  // this provider) fires an /auth/refresh call on its own mount effect to
  // resume a session from the httpOnly cookie. If this wiring only happened
  // in a useEffect here, that refresh call could go out with no
  // X-Tenant-Slug header at all on first page load. configureApiClient()
  // just reassigns a module-level closure, so calling it during render
  // (including twice, under StrictMode) is safe.
  configureApiClient({ getTenantSlug: () => tenantSlug });

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
